
'use client';

// Core Imports
import React from 'react';

// Package Imports
import classNames from 'classnames';

// Function Imports
import { formatCurrency } from '@/lib/functions/formatCurrency';
import { calculateSavingsPercentage } from '@/lib/functions/calculateSavingsPercentage';

// Style Imports
import styles from './productPrice.module.scss';

interface Props {
  unitPrice: number;
  unitPricePromoDiscount?: number;
  unitPriceKeyclubDiscount?: number;
  casePrice?: number;
  caseQuantity?: number;
  casePricePromoDiscount?: number;
  casePriceKeyclubDiscount?: number;
  isCasePrice?: boolean;
  isKeyclubMember?: boolean;
  className?: string;
}

/*
 * ProductPrice
 *
 * Renders pricing for either a single unit or a case pack.
 * - Promo and Key Club dollar-off discounts stack.
 * - Key Club applies only when `isKeyclubMember` is true.
 * - Unit and Case prices are toggled by the 'isCasePrice' prop.
 * - Percent-saved is calculated from the combined discount.
 */
const ProductPrice: React.FC<Props> = ({
  unitPrice,
  unitPricePromoDiscount = 0,
  unitPriceKeyclubDiscount = 0,
  casePrice = unitPrice,
  caseQuantity = 1,
  casePricePromoDiscount = 0,
  casePriceKeyclubDiscount = 0,
  isKeyclubMember = false,
  isCasePrice = false,
  className,
}) => {

  const pricing = {
    unitPrice,
    unitPriceDiscounts: {
      promo: unitPricePromoDiscount || 0,
      keyclub: unitPriceKeyclubDiscount || 0,
    },
    caseQuantity,
    casePrice,
    casePriceDiscounts: {
      standard: Math.abs((unitPrice * caseQuantity) - casePrice),
      promo: casePricePromoDiscount > 0 ? casePrice - casePricePromoDiscount : 0,
      keyclub: casePriceKeyclubDiscount > 0 ? casePriceKeyclubDiscount : 0,
    },
  };

  const activePricing = {
    unitPrice: Math.max(
      unitPrice 
      - pricing.unitPriceDiscounts.promo 
      - (isKeyclubMember ? pricing.unitPriceDiscounts.keyclub : 0),
      0
    ),
    casePrice: Math.max(
      (unitPrice * caseQuantity) 
      - pricing.casePriceDiscounts.standard 
      - pricing.casePriceDiscounts.promo 
      - (isKeyclubMember ? pricing.casePriceDiscounts.keyclub : 0),
      0
    ),
  };

  const nonDiscountedCasePrice = unitPrice * caseQuantity;

  const activeSavingsPercentage = isCasePrice
    ? calculateSavingsPercentage(nonDiscountedCasePrice, activePricing.casePrice)
    : calculateSavingsPercentage(pricing.unitPrice, activePricing.unitPrice);

  // Determine which discount flags we need to display
  const hasPromo = isCasePrice
    ? pricing.casePriceDiscounts?.promo > 0
    : pricing.unitPriceDiscounts.promo > 0;

  const hasKeyclub = isCasePrice
    ? pricing.casePriceDiscounts?.keyclub > 0
    : pricing.unitPriceDiscounts.keyclub > 0;

  const eligibleForKeyclub = hasKeyclub && isKeyclubMember;
  const eligibleForKeyclubUpsell = hasKeyclub && !isKeyclubMember && !hasPromo;

  const showKeyclubBanner = eligibleForKeyclub || eligibleForKeyclubUpsell;
  const showKeyclubColors = showKeyclubBanner;
  const showKeyclubDiscount = eligibleForKeyclub;

  const showPromoBanner = hasPromo && !showKeyclubBanner;
  const showPromoColors = hasPromo && !showKeyclubColors;
  const showPromoDiscount = hasPromo;

  return (
    <div
      className={classNames(
        styles.productPrice,
        className && styles[className],
      )}
    >
      <bdi>
        <div className={styles.productPriceContainer}>
          { showKeyclubBanner && (
            <span className={styles.keyclubLabel}>
              <span>Rewards Price Eligible</span>
            </span>
          )}
          { showPromoBanner && (
            <span className={styles.saleLabel}>
              <span>Sale</span>
            </span>
          )}
          <div>
            <span
              className={classNames(
                styles.price,
                showPromoColors && styles.promoPrice,
                showKeyclubColors && styles.keyclubPrice,
              )}
            >
              <span className={styles.currencySymbol}>$</span>
              {formatCurrency(isCasePrice ? activePricing.casePrice : activePricing.unitPrice, false, true)}
            </span>
            { (showPromoDiscount || showKeyclubDiscount) && (
              <>
                <del className={styles.originalPriceContainer}>
                  <span className={styles.originalPrice}>
                    <span className={styles.currencySymbol}>$</span>
                    {formatCurrency(isCasePrice ? pricing.casePrice : pricing.unitPrice, false, true)}
                  </span>
                </del>
              </>
            )}
            {isCasePrice ? (
              <p className={styles.priceFor}>
                Case of {caseQuantity}
                {activeSavingsPercentage != null && (
                  <span className={styles.percentageSaved}> – Save {activeSavingsPercentage}%</span>
                )}
              </p>
            ) : (
              <p className={styles.priceFor}>Each</p>
            )}
          </div>
        </div>
      </bdi>
    </div>
  );
};

export default ProductPrice;
